import type {
  Entity,
  Relationship,
} from "@adsk/acc-reference-picker/es/types/types";

import { ProjectId, RelationshipsIntersectResponse } from "../types";
import type { AssetsHttpClient } from "./createAssetsHttpClient";

/**
 * Creates a relationship-service client from an already-configured
 * `AssetsHttpClient` instance (see T001.4.5 design note in
 * `createAssetsHttpClient.ts`).
 *
 * @example
 * ```ts
 * const relHttp = createAssetsHttpClient({
 *   fetch: (url, init) => fetchWithToken(url, { ...init, headers: { region: getRegion(), ...init?.headers } }),
 *   baseUrl: getRelationshipServiceUrl(),
 * });
 * const client = createRelationshipClient(relHttp, projectId);
 * ```
 */
export const createRelationshipClient = (
  http: AssetsHttpClient,
  projectId: ProjectId
) => ({
  fetchRelationshipsBySourceId: async <T extends Entity = Entity>(
    ids: T["id"][],
    type: T["type"],
    domain: T["domain"] = "autodesk-bim360-asset"
  ): Promise<Relationship[]> => {
    const fetchedRelationships: Relationship[] = [];

    const fetchRelationships = async (
      continuationToken?: string
    ): Promise<Relationship[]> => {
      const { relationships, page }: RelationshipsIntersectResponse =
        await http.post(
          `${http.baseUrl}/v2/containers/${projectId}/relationships:intersect${
            continuationToken ? `?continuationToken=${continuationToken}` : ""
          }`,
          {
            headers: {
              "Content-Type": "application/json; charset=utf-8",
            },
            body: JSON.stringify({
              entities: ids.map((id) => ({
                id,
                type,
                domain,
              })),
            }),
          }
        );

      const token = page && page.continuationToken;
      fetchedRelationships.push(...relationships);

      if (token) {
        return fetchRelationships(token);
      }

      return fetchedRelationships;
    };

    return fetchRelationships();
  },
});
